import React, { useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI, ScreenSize } from "@wwimmo/ui";
import { IRealestateVisa } from "src/stores/krediflow/InvoiceTypes";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { RealestateVisaUserField } from "src/screens/krediflowSettings/realestateVisas/userField/RealestateVisaUserField";
import styles from "./RealestateVisaList.module.css";

interface RealestateVisaListProps {
    realestateVisaItems: IRealestateVisa[];
}

const RealestateVisaList = (props: RealestateVisaListProps) => {
    const { t } = useTranslation();
    const { uiStore, navStore } = useContext(RootStoreContext);

    const { realestateVisaItems } = props;

    const listIdentifier = "RealestateVisaList";

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(listIdentifier);

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [0.075, 0.175, 0.25, 0.25, 0.25];
        const columnWidthsMobile = [0.075, 0.175, 0.25, 0.25, 0.25];

        return uiStore.currentScreenSize >= SCREEN_SIZE.LAPTOP_L ? columnWidthsDesktop : columnWidthsMobile;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    const renderItem = useCallback(
        (realestateVisa: IRealestateVisa, screenSize: ScreenSize) => {
            const { realestate } = realestateVisa;

            const realestateDetails = (
                <div>
                    <div>{realestate.name}</div>
                    {realestate.zip || realestate.city ? (
                        <div>{`${realestate.zip} ${realestate.city}`.trim()}</div>
                    ) : undefined}
                </div>
            );

            let listRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={columnWidths[0]} value={realestate.number} />
                            <UI.List.Cell key={"c-2"} colspan={columnWidths[1]} value={realestateDetails} />
                            <UI.List.Cell
                                key={"c-3"}
                                colspan={columnWidths[2]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={1} />}
                                className={styles.VisaColumn}
                            />
                            <UI.List.Cell
                                key={"c-4"}
                                colspan={columnWidths[3]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={2} />}
                                className={styles.VisaColumn}
                            />
                            <UI.List.Cell
                                key={"c-5"}
                                colspan={columnWidths[4]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={3} />}
                                className={styles.VisaColumn}
                            />
                        </UI.List.Row>
                    ];
                    break;

                case ScreenSize.MOBILE:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={columnWidths[0]} value={realestate.number} />
                            <UI.List.Cell key={"c-2"} colspan={columnWidths[1]} value={realestateDetails} />
                            <UI.List.Cell
                                key={"c-3"}
                                colspan={columnWidths[2]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={1} />}
                            />
                            <UI.List.Cell
                                key={"c-4"}
                                colspan={columnWidths[3]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={2} />}
                            />
                            <UI.List.Cell
                                key={"c-5"}
                                colspan={columnWidths[4]}
                                value={<RealestateVisaUserField realestateId={realestate.id} visaNumber={3} />}
                            />
                        </UI.List.Row>
                    ];
                    break;
            }

            return (
                <UI.List.Item
                    key={`realestatevisalist-entry-${realestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                />
            );
        },
        [columnWidths]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            let headerRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    headerRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={columnWidths[0]} value={t("labels.number_abr")} />
                            <UI.List.Cell key={"c-2"} colspan={columnWidths[1]} value={t("screens.realestate.title")} />
                            <UI.List.Cell
                                key={"c-3"}
                                colspan={columnWidths[2]}
                                value={`${t("screens.kredi_flow.visa")} 1`}
                            />
                            <UI.List.Cell
                                key={"c-4"}
                                colspan={columnWidths[3]}
                                value={`${t("screens.kredi_flow.visa")} 2`}
                            />
                            <UI.List.Cell
                                key={"c-5"}
                                colspan={columnWidths[4]}
                                value={`${t("screens.kredi_flow.visa")} 3`}
                            />
                        </UI.List.Row>
                    ];
                    break;

                case ScreenSize.MOBILE:
                    headerRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={columnWidths[0]} value={t("labels.number_abr")} />
                            <UI.List.Cell key={"c-2"} colspan={columnWidths[1]} value={t("screens.realestate.title")} />
                            <UI.List.Cell
                                key={"c-3"}
                                colspan={columnWidths[2]}
                                value={`${t("screens.kredi_flow.visa")} 1`}
                            />
                            <UI.List.Cell
                                key={"c-4"}
                                colspan={columnWidths[3]}
                                value={`${t("screens.kredi_flow.visa")} 2`}
                            />
                            <UI.List.Cell
                                key={"c-5"}
                                colspan={columnWidths[4]}
                                value={`${t("screens.kredi_flow.visa")} 3`}
                            />
                        </UI.List.Row>
                    ];
                    break;
            }

            return <UI.List.Header rows={headerRows} />;
        },
        [t, columnWidths]
    );

    return (
        <UI.Card flex>
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={realestateVisaItems}
                renderItem={renderItem}
                renderHeader={renderHeader}
                scrollPosition={listParameters?.scrollPosition ?? undefined}
                itemsPerPage={20}
            />
        </UI.Card>
    );
};

export default observer(RealestateVisaList);
